import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import "./Orders.css"

const Orders = () => {
  // States for order management
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentOrder, setCurrentOrder] = useState({
    id: null,
    supplierId: '',
    orderDate: new Date().toISOString().substr(0, 10), // Today's date in YYYY-MM-DD format
    expectedDeliveryDate: '',
    status: 'pending', // pending, delivered, cancelled
    notes: '',
    totalAmount: 0,
    paidAmount: 0,
    items: []
  });
  
  // State for order items in form
  const [orderItems, setOrderItems] = useState([{
    productId: '',
    quantity: 1,
    unitPrice: 0,
    totalPrice: 0
  }]);
  
  // State for file uploads
  const [selectedFile, setSelectedFile] = useState(null);
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', or 'view'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [currentInvoiceUrl, setCurrentInvoiceUrl] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPayment, setFilterPayment] = useState('all'); // all, paid, unpaid

  // Order status options
  const statusOptions = [
    { value: 'beklemede', label: 'Beklemede' },
    { value: 'onaylandı', label: 'Onaylandı' },
    { value: 'gönderildi', label: 'Gönderildi' },
    { value: 'teslim_alındı', label: 'Teslim Alındı' },
    { value: 'iptal_edildi', label: 'İptal Edildi' }
  ];

  // Fetch orders with filters
  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Prepare filter parameters
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterSupplier) filters.supplierId = filterSupplier;
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterDateFrom) filters.startDate = filterDateFrom;
      if (filterDateTo) filters.endDate = filterDateTo;
      if (filterPayment === 'paid') filters.isPaid = true;
      if (filterPayment === 'unpaid') filters.isPaid = false;

      console.log('Loading orders...', filters);
      const response = await apiService.getOrders(filters);
      console.log('Orders response:', response);
      setOrders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers for dropdown
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.getSuppliers({ isActive: true });
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      // No need to set error as this is secondary data
    }
  };

  // Fetch products for dropdown
  const fetchProducts = async () => {
    try {
      const response = await apiService.getProducts({ isActive: true });
      setProducts(response.data);
    } catch (err) {
      console.error('Error loading products:', err);
      // No need to set error as this is secondary data
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchOrders();
    fetchSuppliers();
    fetchProducts();
  }, []);

  // Reload orders when filters change
  useEffect(() => {
    fetchOrders();
  }, [searchTerm, filterSupplier, filterStatus, filterDateFrom, filterDateTo, filterPayment]);

  // Handle order form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setCurrentOrder({
      ...currentOrder,
      [name]: type === 'checkbox' ? checked : value
    });
  };


  useEffect(() => {
    console.log('Fetching orders with dates:');
    console.log('filterDateFrom:', filterDateFrom);
    console.log('filterDateTo:', filterDateTo);
    fetchOrders();
  }, [searchTerm, filterSupplier, filterStatus, filterDateFrom, filterDateTo, filterPayment]);
  
  
  // Handle order item changes
  const handleOrderItemChange = (index, field, value) => {
    const updatedItems = [...orderItems];
    updatedItems[index][field] = value;
    
    // If product or quantity or unitPrice changes, recalculate totalPrice
    if (field === 'productId' || field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : updatedItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : updatedItems[index].unitPrice;
      
      // If productId changed, get the default unit price from products list
      if (field === 'productId' && value) {
        const selectedProduct = products.find(p => p.id === parseInt(value));
        if (selectedProduct && selectedProduct.unitPrice) {
          updatedItems[index].unitPrice = selectedProduct.unitPrice;
          updatedItems[index].totalPrice = quantity * selectedProduct.unitPrice;
        }
      } else {
        updatedItems[index].totalPrice = quantity * unitPrice;
      }
    }
    
    setOrderItems(updatedItems);
    
    // Recalculate order total
    const total = updatedItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    setCurrentOrder({
      ...currentOrder,
      totalAmount: total
    });
  };

  // Add a new order item row
  const handleAddOrderItem = () => {
    setOrderItems([
      ...orderItems,
      {
        productId: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0
      }
    ]);
  };

  // Remove an order item row
  const handleRemoveOrderItem = (index) => {
    if (orderItems.length === 1) {
      // Don't allow removing the last item
      return;
    }
    
    const updatedItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(updatedItems);
    
    // Recalculate order total
    const total = updatedItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    setCurrentOrder({
      ...currentOrder,
      totalAmount: total
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Upload invoice document
  const uploadInvoice = async (orderId) => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }
    
    const formData = new FormData();
    formData.append('invoice', selectedFile);
    
    try {
      const response = await apiService.uploadOrderDocument(orderId, formData);
      console.log('Upload response:', response);
      
      // Refresh orders to get updated document URLs
      fetchOrders();
      
      // Reset file selection
      setSelectedFile(null);
      setUploadProgress(0);
      
      return response.data.fileUrl;
    } catch (err) {
      console.error('Error uploading invoice:', err);
      alert('Failed to upload invoice: ' + 
        (err.response?.data?.error || err.message));
      setUploadProgress(0);
      return null;
    }
  };

  // Show invoice in modal
  const showInvoice = (url) => {
    setCurrentInvoiceUrl(url);
    setInvoiceModalVisible(true);
  };

  // Submit order form
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validate form
    if (!currentOrder.supplierId) {
      setError('Please select a supplier');
      return;
    }
  
    try {
      // Tarih değerlerini düzgün formata çevir
      let orderData = {
        ...currentOrder,
        status: 'beklemede', // veya status bir enum değeriyle eşleştirilecek
        orderItems: orderItems.filter(item => item.productId && item.quantity > 0)
      };
      
      // Boş tarih değerlerini null yap
      if (!orderData.expectedDeliveryDate || orderData.expectedDeliveryDate === "") {
        orderData.expectedDeliveryDate = null;
      }
      
      // Diğer tüm tarih alanları için aynı kontrolü yapın
      if (!orderData.orderDate || orderData.orderDate === "") {
        orderData.orderDate = new Date().toISOString().substr(0, 10); // Bugünün tarihi
      }
      
      console.log('Sending order data:', orderData); // Debug için
      
      let orderId;
      
      if (modalMode === 'add') {
        // Create new order
        const response = await apiService.createOrder(orderData);
        orderId = response.data.id;
      } else {
        // Update existing order
        const response = await apiService.updateOrder(currentOrder.id, orderData);
        orderId = currentOrder.id;
      }
  
      // Upload invoice if a file is selected
      if (selectedFile && orderId) {
        await uploadInvoice(orderId);
      }
  
      // Success actions
      setModalVisible(false);
      resetForm();
      fetchOrders();
    } catch (err) {
      console.error('Error saving order:', err);
      setError('Failed to save order: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  // Delete order
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    try {
      await apiService.deleteOrder(id);
      fetchOrders();
    } catch (err) {
      console.error('Error deleting order:', err);
      setError('Failed to delete order: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Mark order as paid or update payment amount
  const handlePayment = async (order, amount) => {
    try {
      // Create payment record
      await apiService.createPayment({
        orderId: order.id,
        amount: amount,
        paymentDate: new Date().toISOString().substr(0, 10),
        paymentMethod: 'cash', // Default method
        notes: 'Payment added from orders page'
      });
      
      // Refresh orders
      fetchOrders();
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Failed to process payment: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // View order details
  const handleView = (order) => {
    setCurrentOrder({
      ...order,
      supplierId: order.supplierId || ''
    });
    setOrderItems(order.OrderItems || []);
    setModalMode('view');
    setModalVisible(true);
  };

  // Edit order
  const handleEdit = (order) => {
    setCurrentOrder({
      id: order.id,
      supplierId: order.supplierId || '',
      orderDate: order.orderDate || new Date().toISOString().substr(0, 10),
      expectedDeliveryDate: order.expectedDeliveryDate || '',
      status: order.status || 'pending',
      notes: order.notes || '',
      totalAmount: order.totalAmount || 0,
      paidAmount: order.paidAmount || 0
    });
    
    // Set order items
    if (order.OrderItems && order.OrderItems.length > 0) {
      setOrderItems(order.OrderItems.map(item => ({
        id: item.id,
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice
      })));
    } else {
      // Default empty item
      setOrderItems([{
        productId: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0
      }]);
    }
    
    setModalMode('edit');
    setModalVisible(true);
  };

  // Open add order modal
  const handleAdd = () => {
    resetForm();
    setModalMode('add');
    setModalVisible(true);
  };

// Reset order form
const resetForm = () => {
    setCurrentOrder({
      id: null,
      supplierId: '',
      orderDate: new Date().toISOString().substr(0, 10),
      expectedDeliveryDate: '',
      status: 'beklemede', // 'pending' yerine 'beklemede' kullanın
      notes: '',
      totalAmount: 0,
      paidAmount: 0
    });
    
    setOrderItems([{
      productId: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0
    }]);
    
    setSelectedFile(null);
    setUploadProgress(0);
  };
  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Get supplier name by ID
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : '-';
  };

  // Get product name by ID
  const getProductName = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return product ? product.name : '-';
  };

  // Calculate payment status
  const getPaymentStatus = (order) => {
    if (!order.totalAmount) return 'N/A';
    
    const paidAmount = order.paidAmount || 0;
    const remainingAmount = order.totalAmount - paidAmount;
    
    if (paidAmount <= 0) {
      return <span className="status-badge payment-unpaid">Unpaid</span>;
    } else if (remainingAmount <= 0) {
      return <span className="status-badge payment-paid">Paid</span>;
    } else {
      const percentage = Math.round((paidAmount / order.totalAmount) * 100);
      return <span className="status-badge payment-partial">Partial ({percentage}%)</span>;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge status-pending">Pending</span>;
      case 'delivered':
        return <span className="status-badge status-delivered">Delivered</span>;
      case 'cancelled':
        return <span className="status-badge status-cancelled">Cancelled</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  return (
    <div className="tab-content">
      <div className="content-header">
        <h3>Orders</h3>
        <button 
          className="add-button"
          onClick={handleAdd}
        >
          <span className="icon">+</span>
          <span className="button-text">Add New Order</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search orders..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterSupplier">Supplier:</label>
          <select 
            id="filterSupplier" 
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
          >
            <option value="">All</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterStatus">Status:</label>
          <select 
            id="filterStatus" 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All</option>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterPayment">Payment:</label>
          <select 
            id="filterPayment" 
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
          >
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid/Partial</option>
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterDateFrom">From:</label>
          <input 
            type="date" 
            id="filterDateFrom" 
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterDateTo">To:</label>
          <input 
            type="date" 
            id="filterDateTo" 
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="orders-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Supplier</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total Amount</th>
                <th>Payment Status</th>
                <th>Invoice</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">No orders found.</td>
                </tr>
              ) : (
                orders.map(order => (
                  <tr key={order.id} className={order.status === 'cancelled' ? 'inactive-row' : 'active-row'}>
                    <td>{order.id}</td>
                    <td>{getSupplierName(order.supplierId)}</td>
                    <td>{formatDate(order.orderDate)}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>{formatCurrency(order.totalAmount)}</td>
                    <td>{getPaymentStatus(order)}</td>
                    <td>
                      {order.invoiceUrl ? (
                        <button 
                          className="view-button"
                          onClick={() => showInvoice(order.invoiceUrl)}
                          title="View Invoice"
                        >
                          <span className="icon">📄</span>
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="view-button"
                          onClick={() => handleView(order)}
                          title="View Details"
                        >
                          <span className="icon">👁️</span>
                        </button>
                        <button 
                          className="edit-button"
                          onClick={() => handleEdit(order)}
                          title="Edit"
                        >
                          <span className="icon">✎</span>
                        </button>
                        <button 
                          className="payment-button"
                          onClick={() => {
                            const remainingAmount = order.totalAmount - (order.paidAmount || 0);
                            if (remainingAmount <= 0) {
                              alert('This order is already fully paid.');
                              return;
                            }
                            
                            const amount = window.prompt(
                              `Enter payment amount (Remaining: ${formatCurrency(remainingAmount)}):`, 
                              remainingAmount.toFixed(2)
                            );
                            
                            if (amount !== null) {
                              handlePayment(order, parseFloat(amount));
                            }
                          }}
                          title="Add Payment"
                          disabled={order.status === 'cancelled'}
                        >
                          <span className="icon">💰</span>
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(order.id)}
                          title="Delete"
                        >
                          <span className="icon">✕</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal wide-modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'add' ? 'Add New Order' : 
                 modalMode === 'edit' ? 'Edit Order' : 'View Order Details'}
              </h3>
              <button 
                className="close-button"
                onClick={() => setModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Order Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="supplierId">Supplier *</label>
                    <select 
                      id="supplierId" 
                      name="supplierId" 
                      value={currentOrder.supplierId} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select 
                      id="status" 
                      name="status" 
                      value={currentOrder.status} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="orderDate">Order Date *</label>
                    <input 
                      type="date" 
                      id="orderDate" 
                      name="orderDate" 
                      value={currentOrder.orderDate} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="expectedDeliveryDate">Expected Delivery Date</label>
                    <input 
                      type="date" 
                      id="expectedDeliveryDate" 
                      name="expectedDeliveryDate" 
                      value={currentOrder.expectedDeliveryDate} 
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea 
                    id="notes" 
                    name="notes" 
                    value={currentOrder.notes} 
                    onChange={handleInputChange}
                    rows="3"
                    disabled={modalMode === 'view'}
                  ></textarea>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Order Items</h4>
                
                <div className="order-items-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        {modalMode !== 'view' && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {modalMode === 'view' ? (
                              getProductName(item.productId)
                            ) : (
                              <select 
                                value={item.productId} 
                                onChange={(e) => handleOrderItemChange(index, 'productId', e.target.value)}
                                required
                              >
                                <option value="">Select a product</option>
                                {products.map(product => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} ({formatCurrency(product.unitPrice)}/{product.unitType})
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {modalMode === 'view' ? (
                              item.quantity
                            ) : (
                              <input 
                                type="number" 
                                value={item.quantity} 
                                onChange={(e) => handleOrderItemChange(index, 'quantity', parseFloat(e.target.value))}
                                min="0.01"
                                step="0.01"
                                required
                              />
                            )}
                          </td>
                          <td>
                            {modalMode === 'view' ? (
                              formatCurrency(item.unitPrice)
                            ) : (
                              <input 
                                type="number" 
                                value={item.unitPrice} 
                                onChange={(e) => handleOrderItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                                min="0.01"
                                step="0.01"
                                required
                              />
                            )}
                          </td>
                          <td>{formatCurrency(item.totalPrice)}</td>
                          {modalMode !== 'view' && (
                            <td>
                              <button 
                                type="button" 
                                className="delete-button"
                                onClick={() => handleRemoveOrderItem(index)}
                                disabled={orderItems.length === 1}
                                title="Remove Item"
                              >
                                <span className="icon">✕</span>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={modalMode === 'view' ? 3 : 4} className="text-right">
                          <strong>Total:</strong>
                        </td>
                        <td>
                          <strong>{formatCurrency(currentOrder.totalAmount)}</strong>
                        </td>
                      </tr>
                      {currentOrder.paidAmount > 0 && (
                        <>
                          <tr>
                            <td colSpan={modalMode === 'view' ? 3 : 4} className="text-right">
                              Paid Amount:
                            </td>
                            <td>{formatCurrency(currentOrder.paidAmount)}</td>
                          </tr>
                          <tr>
                            <td colSpan={modalMode === 'view' ? 3 : 4} className="text-right">
                              <strong>Remaining:</strong>
                            </td>
                            <td>
                              <strong>{formatCurrency(currentOrder.totalAmount - currentOrder.paidAmount)}</strong>
                            </td>
                          </tr>
                        </>
                      )}
                    </tfoot>
                  </table>
                  
                  {modalMode !== 'view' && (
                    <div className="add-item-button">
                      <button 
                        type="button" 
                        className="secondary-button"
                        onClick={handleAddOrderItem}
                      >
                        <span className="icon">+</span> Add Item
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {modalMode !== 'view' && (
                <div className="form-section">
                  <h4>Invoice Upload</h4>
                  
                  <div className="form-group">
                    <label htmlFor="invoice">Upload Invoice (PDF, JPG, PNG)</label>
                    <input 
                      type="file" 
                      id="invoice" 
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </div>
                  
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setModalVisible(false)}
                >
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
                
                {modalMode !== 'view' && (
                  <button 
                    type="submit" 
                    className="save-button"
                  >
                    Save
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Viewer Modal */}
      {invoiceModalVisible && (
        <div className="modal-overlay">
          <div className="modal invoice-modal">
            <div className="modal-header">
              <h3>Invoice</h3>
              <button 
                className="close-button"
                onClick={() => setInvoiceModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="invoice-viewer">
              {currentInvoiceUrl.endsWith('.pdf') ? (
                <iframe 
                  src={currentInvoiceUrl} 
                  width="100%" 
                  height="500px"
                  title="Invoice PDF"
                ></iframe>
              ) : (
                <img 
                  src={currentInvoiceUrl} 
                  alt="Invoice" 
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                />
              )}
            </div>
            <div className="form-actions">
              <a 
                href={currentInvoiceUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="secondary-button"
              >
                Open in New Tab
              </a>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setInvoiceModalVisible(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;